/**
 * Masthead billing control. Renders nothing when the backend has no Stripe
 * price configured (config.billing_enabled) -- a deployment that hasn't set
 * up billing yet should not show a button that 503s.
 *
 * Free tier -> "Upgrade to Professional". Checkout now requires an
 * already-authenticated account (see backend routes_billing.py) -- an
 * anonymous click opens the same login/signup dialog as the masthead's own
 * AuthMenu first, then proceeds to the billing-period choice once that
 * succeeds. Paid tier with a real Stripe subscription -> "Manage
 * subscription", straight to Stripe's own Billing Portal.
 */

import { useState } from 'react';
import * as api from '../lib/api';
import { ApiError } from '../lib/api';
import type { AppConfig } from '../lib/types';
import { AuthDialog } from './AuthMenu';

const PERIOD_LABEL: Record<'monthly' | 'annual', string> = {
  monthly: 'Monthly',
  annual: 'Annual',
};

export function UpgradeButton({
  config,
  onAuthChange,
}: {
  config: AppConfig;
  onAuthChange: () => void | Promise<void>;
}) {
  const [authMode, setAuthMode] = useState<'login' | 'signup' | null>(null);
  const [showPeriodDialog, setShowPeriodDialog] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!config.billing_enabled) return null;

  const isFree = config.tier === 'free';
  // Only a Stripe-backed subscription (subscription_status set by the
  // webhook) gets "Manage subscription" -- a tier set by hand through the
  // admin API (comps, deals negotiated outside Stripe) has no Stripe
  // customer to open a portal session for.
  const hasManageablePlan = !isFree && Boolean(config.subscription_status);

  if (!isFree && !hasManageablePlan) return null;

  async function beginCheckout(period: 'monthly' | 'annual') {
    setBusy(true);
    setError(null);
    try {
      const result = await api.startCheckout({ period });
      window.location.href = result.checkout_url;
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not start checkout.');
      setBusy(false);
    }
  }

  async function openPortal() {
    setBusy(true);
    setError(null);
    try {
      const result = await api.openBillingPortal();
      window.location.href = result.portal_url;
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not open the billing portal.');
      setBusy(false);
    }
  }

  if (hasManageablePlan) {
    return (
      <button type="button" className="btn btn-sm" disabled={busy} onClick={openPortal}>
        {busy ? 'Opening…' : 'Manage subscription'}
      </button>
    );
  }

  return (
    <>
      <button
        type="button"
        className="btn btn-sm btn-accent"
        onClick={() => {
          // Checkout needs an actual account to attach the subscription to
          // -- if this browser isn't logged in yet, sign-up comes first
          // (defaulting to that tab rather than login, since most clicks
          // here are a brand-new user's first interaction with billing).
          if (!config.authenticated) setAuthMode('signup');
          else setShowPeriodDialog(true);
        }}
      >
        Upgrade to Professional
      </button>

      {authMode && (
        <AuthDialog
          mode={authMode}
          onCancel={() => setAuthMode(null)}
          onSwitchMode={() => setAuthMode(authMode === 'login' ? 'signup' : 'login')}
          onSuccess={async () => {
            setAuthMode(null);
            await onAuthChange();
            setShowPeriodDialog(true);
          }}
        />
      )}

      {showPeriodDialog && (
        <CheckoutDialog
          periods={config.billing_periods}
          busy={busy}
          error={error}
          onCancel={() => setShowPeriodDialog(false)}
          onSubmit={(period) => void beginCheckout(period)}
        />
      )}
    </>
  );
}

function CheckoutDialog({
  periods,
  busy,
  error,
  onCancel,
  onSubmit,
}: {
  periods: Array<'monthly' | 'annual'>;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (period: 'monthly' | 'annual') => void;
}) {
  const [period, setPeriod] = useState<'monthly' | 'annual'>(periods[0] ?? 'monthly');

  return (
    <div
      className="overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Start a Professional subscription"
    >
      <div className="card">
        <div className="card-head">
          <h2>Start your Professional subscription</h2>
        </div>
        <div className="card-body stack">
          {periods.length > 1 && (
            <div className="stack" style={{ gap: '0.4rem' }}>
              <span className="hint-strong">Billing period</span>
              <div className="row">
                {periods.map((option) => (
                  <label key={option} className="row" style={{ gap: '0.3rem' }}>
                    <input
                      type="radio"
                      name="billing-period"
                      value={option}
                      checked={period === option}
                      onChange={() => setPeriod(option)}
                    />
                    {PERIOD_LABEL[option]}
                  </label>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="hint" style={{ color: 'var(--fail-600, #c0392b)' }}>
              {error}
            </div>
          )}

          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-sm" onClick={onCancel} disabled={busy}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-sm btn-accent"
              disabled={busy}
              onClick={() => onSubmit(period)}
            >
              {busy ? 'Starting…' : 'Continue to Stripe'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
